/**
 * 「収録動画から」の由来と省略 (docs/features/video-import-scenario-key-points-plan.md §5.4)。
 * 作業パネルのシナリオ本文の先頭に折りたたみで出す。表示は保存済みの generation_metadata.source_video だけを読む
 * (遷移時の一時的な state には依存しない。シナリオを開き直しても出る。要点の文言は保存済みのスナップショットから引き、元の編集の digest は読まない)。
 * 内容: 元の編集 (動画エディタへのリンク)・使った話題数・選んだ要点と AI の提案との差・省略した材料・送った要旨の全文 (折りたたみ)。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { describeOmitted } from '../../../features/videoEditor/sourceVideo';
import { describeSourceVideoPoints, readSourceVideoMeta } from './sourceVideo';
import styles from './ScriptImportReportSection.module.css';

interface Props {
  scenario: { id: string; generation_metadata?: Record<string, unknown> | null };
  defaultOpen?: boolean;
}

export function ScenarioSourceVideoSection({ scenario, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [briefOpen, setBriefOpen] = useState(false);
  const meta = readSourceVideoMeta(scenario.generation_metadata);
  if (!meta) return null;
  const omitted = describeOmitted(meta.omitted);
  const label = meta.label || meta.project_name || '収録動画';
  const legacyAppeals = meta.appeals && meta.appeals.length > 0 && !(meta.point_ids && meta.point_ids.length > 0) ? meta.appeals : null;
  const pointsInfo = describeSourceVideoPoints(meta);

  return (
    <section className={styles.section} data-testid="source-video-section">
      <button type="button" className={styles.header} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <ChevronRight size={14} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true" />
        <span className={styles.headerTitle}>収録動画から</span>
        <span className={styles.headerFile}>{label}（{meta.segment_count ?? '?'} / {meta.total_segments ?? '?'}話題）</span>
        {omitted.length > 0 && <span className={styles.pill}>省略あり</span>}
      </button>
      {open && (
        <div className={styles.body}>
          <div className={styles.row}>
            <span className={styles.rowLabel}>元の編集</span>
            <span>
              {meta.project_id ? <Link to={`/studio/video/editor/${meta.project_id}`}>{label}</Link> : label}
              {meta.chars != null && <span className={styles.note}>（要旨 {meta.chars.toLocaleString()}字）</span>}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>要点</span>
            {legacyAppeals ? (
              <span>{legacyAppeals.join(' / ')}</span>
            ) : pointsInfo.selected.length > 0 ? (
              <span data-testid="source-video-points">
                <ol className={styles.list}>
                  {pointsInfo.selected.map((t) => <li key={t}>{t}</li>)}
                </ol>
                {pointsInfo.removed.length > 0 && <span className={styles.note}>提案から外した: {pointsInfo.removed.join(' / ')}</span>}
                {pointsInfo.added.length > 0 && <span className={styles.note}>提案に足した: {pointsInfo.added.join(' / ')}</span>}
                {pointsInfo.removed.length === 0 && pointsInfo.added.length === 0 && meta.suggested_point_ids && (
                  <span className={styles.note}>AIの提案のとおり（想定尺 {meta.duration_sec ?? '?'}秒の既定）</span>
                )}
              </span>
            ) : (
              <span className={styles.note}>記録がありません</span>
            )}
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>省略</span>
            {omitted.length > 0 ? (
              <ul className={styles.list} data-testid="source-video-omitted">
                {omitted.map((o) => <li key={o}>{o}</li>)}
              </ul>
            ) : (
              <span className={styles.note}>なし（根拠の話題の本文は全文を渡しました）</span>
            )}
          </div>
          {meta.brief && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>送った要旨</span>
              <span>
                <button type="button" className="btn-link-small" onClick={() => setBriefOpen((v) => !v)}>
                  {briefOpen ? '閉じる' : '全文を見る'}
                </button>
                {briefOpen && <pre className={styles.brief} data-testid="source-video-brief">{meta.brief}</pre>}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
